import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ComponentAdapterCounterElectric } from "../../class/adapters/component-adapter-counter-electric";
import { WidgetParent } from "../../class/widget-parent";
import { SmartPaletteCounter } from "../../class/smart-palette";
import { ComponentAdapterFactory } from "../../class/adapters/component-adapter-factory";
import { IWidgetExpandable } from "../../interfaces/IWidgetExpandable";
import { CounterHeaderData } from "../../class/counter-header-data";
import { SmartComponentType } from "../../enum/enumSmartComponentType";
import { ElectricCounterMeasurementType } from "../../enum/enumCounterMeasurementType";
import { DateHelper } from "../../class/general-classes/date-helper";
import { CounterHeaderComponent } from "../counter/counter-header/counter-header.component";
import { CounterElectricComponent } from "../counter/counter-electric/counter-electric.component";
import { IonIcon } from "@ionic/angular";

@Component({
    selector: "app-widget-counter-electric",
    templateUrl: "./widget-counter-electric.component.html",
    styleUrls: [
        "./widget-counter-electric.component.scss",
        "../../styles/shared-global.scss",
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CounterHeaderComponent, CounterElectricComponent, IonIcon]
})
export class WidgetCounterElectricComponent
  extends WidgetParent<ComponentAdapterCounterElectric, SmartPaletteCounter>
  implements IWidgetExpandable
{
  private _currentMonthValue: string = "";
  private _previousMonthValue: string = "";

  public isExpanded: boolean = false;
  public areChartsVisible: boolean = false;
  public headerData: CounterHeaderData = null;

  public displayedCounterTypeId: number = -1;
  public secondColorLabel: string = "#c4dee8";

  /**
   * Open or close the widget
   */
  public onClickOpenCloseWidget(): void {
    if (!this.isExpanded) {
      this.onExpandWidget();
    } else {
      this.onCollapseWidget();
    }
  }

  onExpandWidget(): void {
    this.isExpanded = true;
  }

  onCollapseWidget(): void {
    this.isExpanded = false;
  }

  protected setWidgetSmartPalette() {
    // All counters share the same brand-derived palette (see setWidgetSmartPaletteCounterDefault).
    return this.setWidgetSmartPaletteCounterDefault();
  }

  protected override onLanguageChange(): void {
    const now = new Date();
    this._currentMonthValue = this._localeService.formatDate(now, "month").toUpperCase();
    this._previousMonthValue = this._localeService.formatDate(DateHelper.subtract(now, 1, "month"), "month").toUpperCase();

    if (this.headerData) {
      this.headerData.subtitle = this._currentMonthValue;
      this.headerData.title1 = this._translateService.instant("dictionary.producedEnergy");
      this.headerData.title2 = this._translateService.instant("dictionary.absorbedEnergy");
      this.headerData.title3 = this._translateService.instant("dictionary.power");
    }

    if (this.componentAdapter) {
      this.componentAdapter.CurrentMonth = this._currentMonthValue;
      this.componentAdapter.PreviousMonth = this._previousMonthValue;
    }
  }

  protected onComponentLoad(): void {}

  protected onSmartComponentSuccessLoaded(): void {
    const factory = new ComponentAdapterFactory();
    this.loadSummaryData();
    this.componentAdapter = factory.createComponentAdapter(
      this._listSmartComponentSkills,
      ComponentAdapterCounterElectric,
      this._headerSummary
    );
    this.componentAdapter.fillMeasurementMap(
      this._listSmartComponentCounterMeasurements,
      ElectricCounterMeasurementType,
      this.defaultDecimalDigits,
      this._localeService.locale()
    );

    this.prepareHeaderData();
  }

  protected onComponentDestroy(): void {}

  protected onSmartComponentErrorLoading(): void {}

  protected onAddressValueChange(
    instanceGUID: string,
    address: string,
    value: string
  ): void {}

  /**
   * This method prepare and initialise the data passed to the header compoent. These data are:
   * + Taken from the previously fetched property.set of the SmartComponent (Name)
   * + Defined locally as localised text strings based on the counter sub-type (Titles)
   * + Calculated locally (Subtitle)
   */
  private prepareHeaderData(): void | boolean {
    const dataToHeader: CounterHeaderData = new CounterHeaderData();
    if (this.smartComponent.summary) {
      dataToHeader.titleWidget = this._translateService.instant("dictionary.consumptionDistribution");
      dataToHeader.subtitle = "";
    } else {
      dataToHeader.titleWidget = this.componentAdapter.Name;
      dataToHeader.subtitle = this._currentMonthValue;
    }

    //If component title is empty show error loading component
    if (!dataToHeader.titleWidget) {
      return (this._errorLoading = true);
    }

    if (this.componentAdapter.EnergyProduced) {
      dataToHeader.title1 = this._translateService.instant("dictionary.producedEnergy");
      dataToHeader.value1 =
        this.componentAdapter.MeasurementMap.get(
          ElectricCounterMeasurementType[
            ElectricCounterMeasurementType.ELECTRIC_ENERGY_PRODUCED_LAST_MONTH
          ]
        ) || "---";
    }

    if (this.componentAdapter.EnergyAbsorbed) {
      dataToHeader.title2 = this._translateService.instant("dictionary.absorbedEnergy");
      dataToHeader.value2 =
        this.componentAdapter.MeasurementMap.get(
          ElectricCounterMeasurementType[
            ElectricCounterMeasurementType.ELECTRIC_ENERGY_ABSORBED_LAST_MONTH
          ]
        ) || "---";
    }

    if (this.componentAdapter.ActivePower && !this.componentAdapter.Summary) {
      dataToHeader.title3 = this._translateService.instant("dictionary.power");
      dataToHeader.value3 =
        this.componentAdapter.MeasurementMap.get(
          ElectricCounterMeasurementType[
            ElectricCounterMeasurementType.ELECTRIC_ACTIVE_POWER
          ]
        ) || "---";
    }

    this.headerData = dataToHeader;

    this.isExpanded = this._isExpanded;
    this.areChartsVisible = this._isExpanded ? true : false;
  }
}
